import { useState } from 'react';
import { del, post } from '../lib/api';
import { ErrorNote, Sheet } from './ui';
import { money, percent, periodLabel } from '../lib/format';
import type { PeriodSummary } from '../lib/types';

type Row = Omit<PeriodSummary, 'items'>;

/** Records the parents' decision for one week or month. */
export default function PayoutSheet({
  childId,
  row,
  onClose,
  onSaved,
}: {
  childId: number;
  row: Row;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const [amount, setAmount] = useState(row.payout ? row.payout.amount : row.totalAmount);
  const [note, setNote] = useState(row.payout?.note ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function decide(status: 'paid' | 'withheld') {
    setBusy(true);
    setError(null);
    try {
      await post('/api/payouts', {
        childId,
        periodType: row.periodType,
        periodKey: row.periodKey,
        status,
        amount: status === 'paid' ? amount : 0,
        note,
      });
      onSaved();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function clear() {
    setBusy(true);
    try {
      await del(`/api/payouts?childId=${childId}&periodType=${row.periodType}&periodKey=${row.periodKey}`);
      onSaved();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet title={periodLabel(row.periodType, row.periodKey)} onClose={onClose}>
      <ErrorNote error={error} />
      <div className="card">
        <div className="row between">
          <span className="muted small">Chores done</span>
          <strong>
            {row.doneCount}/{row.totalCount} · {percent(row.rate)}
          </strong>
        </div>
        <div className="row between" style={{ marginTop: 6 }}>
          <span className="muted small">Needed to qualify</span>
          <strong>{percent(row.threshold)}</strong>
        </div>
        <div className="row between" style={{ marginTop: 6 }}>
          <span className="muted small">The rules say</span>
          <span className={`badge ${row.qualifies ? 'good' : 'bad'}`}>
            {row.qualifies ? 'Qualifies' : 'Does not qualify'} · {money(row.amount, row.currency)}
          </span>
        </div>
        {(row.extrasApproved > 0 || row.extrasPending > 0) && (
          <div className="row between" style={{ marginTop: 6 }}>
            <span className="muted small">Extras</span>
            <span className="small">
              +{money(row.extrasApproved, row.currency)}
              {row.extrasApproved > row.extrasUnpaid && (
                <span className="muted"> · {money(row.extrasApproved - row.extrasUnpaid, row.currency)} already paid</span>
              )}
              {row.extrasPending > 0 && <span className="muted"> · {money(row.extrasPending, row.currency)} not yet approved</span>}
            </span>
          </div>
        )}
        {row.extrasUnpaid > 0 && (
          <div className="tiny muted" style={{ marginTop: 6 }}>
            The amount below includes {money(row.extrasUnpaid, row.currency)} of extras not paid yet. Marking the
            week paid marks those paid too.
          </div>
        )}
        {row.provisional && (
          <div className="notice warn small" style={{ marginTop: 10 }}>
            This period is still running.
          </div>
        )}
      </div>

      <label className="field" style={{ marginTop: 14 }}>
        <span>Amount to pay</span>
        <input type="number" min={0} value={amount} onChange={(e) => setAmount(Number(e.target.value))} />
      </label>
      <label className="field">
        <span>Note (optional)</span>
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Missed the bins twice" />
      </label>

      <div className="row">
        <button className="btn danger grow" type="button" disabled={busy} onClick={() => void decide('withheld')}>
          Withhold
        </button>
        <button className="btn grow" type="button" disabled={busy} onClick={() => void decide('paid')}>
          Mark as paid
        </button>
      </div>
      {row.payout && (
        <button className="btn ghost block" type="button" style={{ marginTop: 10 }} onClick={clear} disabled={busy}>
          Undo this decision
        </button>
      )}
    </Sheet>
  );
}
